import type React from 'react'

import { useTranslations } from 'next-intl'
import { useCallback, useEffect } from 'react'

import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { useEventBusContext } from '@maestro/core/event/event-bus.context'
import { EventType } from '@maestro/data-layer'
import { theme } from '@maestro/local/theme'
import { useHash } from '@pearlconvert/ui/hooks/use-hash'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

export function useForgotPassword() {
  const { closeModal, openModal } = useModals()
  const { addOrUpdateHashParam, removeHashParam, getHashParamValue } = useHash()
  const { publish } = useEventBusContext()
  const t = useTranslations('commerce-blocks')
  const { session } = useMaestroSession()

  const { modalKey: forgotPasswordModalKey } = theme().resetPasswordPage

  useEffect(() => {
    const hasForgotPasswordHash =
      getHashParamValue(forgotPasswordModalKey) !== undefined
    const isUserLoggedIn = !!session?.user?.id

    if (hasForgotPasswordHash && isUserLoggedIn) {
      removeHashParam(forgotPasswordModalKey)
    }
  }, [
    forgotPasswordModalKey,
    getHashParamValue,
    removeHashParam,
    session?.user?.id,
  ])

  const handleOpenForgotPassword = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      publish(EventType.SLIDE_IN_OPENED, {
        title: t('auth.forgot-password.title'),
        slideInType: 'reset_password',
        action: 'open',
      })
      addOrUpdateHashParam('forgot-password')
    },
    [addOrUpdateHashParam, publish, t]
  )

  const handleResetSuccess = useCallback(
    (email?: string) => {
      if (email) {
        localStorage.setItem('maestro:reset-password:email', email)
      }
      openModal('forgot-password-confirmation')
      setTimeout(() => {
        removeHashParam('forgot-password')
      }, 1000)
    },
    [openModal, removeHashParam]
  )

  const handleBackToLogin = useCallback(() => {
    closeModal('forgot-password-confirmation')
    removeHashParam('forgot-password')
  }, [closeModal, removeHashParam])

  return {
    handleBackToLogin,
    handleOpenForgotPassword,
    handleResetSuccess,
    forgotPasswordModalKey,
  }
}
